import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Loader2, Clock, Calendar, User, CheckCircle, XCircle, FileText
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getRequestTypeLabel } from '@/lib/attendanceTypes';

// Leave type colors
const getLeaveTypeColor = (type) => {
  const colors = {
    'annual_leave': 'bg-blue-100 text-blue-800 border-blue-200',
    'sick_leave': 'bg-red-100 text-red-800 border-red-200',
    'unpaid_leave': 'bg-gray-100 text-gray-800 border-gray-200',
    'work_from_home': 'bg-purple-100 text-purple-800 border-purple-200',
    'compassionate_leave': 'bg-orange-100 text-orange-800 border-orange-200',
    'parental_leave': 'bg-pink-100 text-pink-800 border-pink-200',
    'training': 'bg-green-100 text-green-800 border-green-200'
  };
  return colors[type] || colors['annual_leave'];
};

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short'
  });
};

const formatDateRange = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (start.toDateString() === end.toDateString()) {
    return formatDate(startDate);
  }
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

export function PendingLeaveRequestsTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { userProfile, company, can } = useAuth();
  const canManageAttendance = can?.('attendance.manage');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadPendingRequests = async () => {
      try {
        setLoading(true);

        // Get all company user IDs
        const { data: companyUsers } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('company_id', company.id);

        const companyUserIds = (companyUsers || []).map(u => u.id);

        if (companyUserIds.length === 0) {
          setRequests([]);
          setLoading(false);
          return;
        }

        // Load pending leave requests
        const { data: requestsData, error: requestsError } = await supabase
          .from('leave_requests')
          .select(`
            *,
            user_profiles!leave_requests_user_id_fkey(
              first_name,
              last_name,
              email
            )
          `)
          .in('user_id', companyUserIds)
          .eq('status', 'pending')
          .order('created_date', { ascending: false })
          .limit(10);

        if (requestsError) throw requestsError;

        setRequests(requestsData || []);
      } catch (error) {
        console.error('Failed to load pending requests:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPendingRequests();
  }, [company?.id]);

  const goToAllRequests = () => {
    navigate('/attendance/requests');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <div className="bg-green-100 p-3 rounded-full mb-3">
          <CheckCircle className="h-6 w-6 text-green-600" />
        </div>
        <p className="text-sm font-medium text-gray-900">All caught up!</p>
        <p className="text-xs text-gray-500 mt-1">No pending requests</p>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header with count badge */}
        <div className="flex items-center justify-between mb-2 pb-2 border-b shrink-0">
          <div className="flex items-center gap-2">
            <Badge className="bg-yellow-500 text-white">
              {requests.length}
            </Badge>
            <span className="text-xs text-gray-600">awaiting review</span>
          </div>
        </div>

        {/* Requests list */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="space-y-2 pr-2">
            {requests.map((request) => (
              <Tooltip key={request.id}>
                <TooltipTrigger asChild>
                  <div
                    onClick={goToAllRequests}
                    className={cn(
                      "p-2 rounded-lg border bg-yellow-50 border-yellow-200",
                      "hover:shadow-md cursor-pointer transition-all"
                    )}
                  >
                    <div className="flex items-start gap-2">
                      {/* Avatar */}
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-semibold text-[10px]">
                          {request.user_profiles?.first_name?.[0]}{request.user_profiles?.last_name?.[0]}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="text-xs font-medium text-gray-900 truncate">
                            {request.user_profiles?.first_name} {request.user_profiles?.last_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge className={cn("text-[10px] px-1.5 py-0", getLeaveTypeColor(request.leave_type))}>
                            {getRequestTypeLabel(request.leave_type)}
                          </Badge>
                          <span className="text-[10px] text-gray-600">
                            {formatDateRange(request.start_date, request.end_date)}
                          </span>
                        </div>
                      </div>

                      {/* Status indicator */}
                      <Clock className="h-4 w-4 text-yellow-600 flex-shrink-0" />
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs p-0 bg-white">
                  <div className="p-3 space-y-2">
                    <div className="font-semibold text-sm text-gray-900">
                      {request.user_profiles?.first_name} {request.user_profiles?.last_name}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={cn("text-xs", getLeaveTypeColor(request.leave_type))}>
                        {getRequestTypeLabel(request.leave_type)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-600">
                      <Calendar className="h-3 w-3" />
                      {formatDateRange(request.start_date, request.end_date)}
                    </div>
                    {request.user_note && (
                      <div className="flex items-start gap-1 text-xs text-gray-600">
                        <FileText className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-2">{request.user_note}</span>
                      </div>
                    )}
                    <div className="text-[10px] text-gray-500 pt-1 border-t">
                      Submitted: {new Date(request.created_date).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'short', year: 'numeric'
                      })}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </ScrollArea>

        {/* Footer - link to all requests */}
        <button
          onClick={goToAllRequests}
          className="mt-2 pt-2 border-t flex items-center justify-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-medium transition-colors shrink-0"
        >
          <Clock className="h-3.5 w-3.5" />
          <span>View All Requests</span>
        </button>
      </div>
    </TooltipProvider>
  );
}
